import { Avatar, AvatarGroup, AVATAR_SIZES } from "@shared/ui/Avatar";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Overlay from "@shared/ui/Overlay";
import Tooltip from "@shared/ui/Tooltip";
import React, { useRef } from "react";
import { useIntl } from "react-intl";

import { ASSIGNEES_AVATARS_MAX_LENGTH } from "#/src/constants";

// Assignees arrive either as `{ _id, name }` objects or as plain name strings.
// Never fall back to the raw assignee: an object with a missing/null `name`
// would reach Avatar as a non-string and crash it.
const getAssigneeName = (assignee) => {
  if (typeof assignee === "string") return assignee.trim();

  return typeof assignee?.name === "string" ? assignee.name.trim() : "";
};

const TicketItemAssigneesList = ({
  assignees = [],
  tooltipPlace = "top",
  maxLength = ASSIGNEES_AVATARS_MAX_LENGTH,
  className = "",
  avatarSize = AVATAR_SIZES.SMALL,
  isBoardView = false,
}) => {
  const { messages } = useIntl();

  const [showOverlay, setShowOverlay] = React.useState(false);

  const avatarRef = useRef(null);

  // console.log(showTooltips)

  if (isBoardView && assignees.length === 1) {
    const resolvedName = getAssigneeName(assignees[0]);
    const assigneeName = resolvedName || messages?.common?.noAssignee || "";
    return (
      <>
        <div className="single-assignee-user-avatar-container">
          <div
            onMouseEnter={() => setShowOverlay(true)}
            onMouseLeave={() => setShowOverlay(false)}
          >
            <Avatar
              size={avatarSize}
              hasNoUser={!resolvedName}
              name={assigneeName}
              showName={false}
              containerClassName={className}
              avatarRef={avatarRef}
            />
          </div>
        </div>
        <Overlay
          arrowClasses="before:bg-secondary before:shadow-alert"
          className="bg-secondary p-2 rounded shadow-alert w-fit z-10"
          containerRef={document.body}
          offset={[0, 10]}
          placement={tooltipPlace}
          showOverlay={showOverlay}
          targetRef={avatarRef}
        >
          <BodyText color="text-inverse" size={BODY_TEXT_SIZES.X_SMALL}>
            {assigneeName}
          </BodyText>
        </Overlay>
      </>
    );
  }

  if (isBoardView && assignees.length > 1) {
    const resolvedFirstName = getAssigneeName(assignees[0]);
    const firstAssigneeName =
      resolvedFirstName || messages?.common?.noName || "";
    const allAssigneeNames = assignees
      .map(
        (assignee) =>
          getAssigneeName(assignee) || messages?.common?.noName || "",
      )
      .join(", ");

    return (
      <>
        <div
          className="flex"
          onMouseEnter={() => setShowOverlay(true)}
          onMouseLeave={() => setShowOverlay(false)}
        >
          <Avatar
            size={avatarSize}
            hasNoUser={!resolvedFirstName}
            name={firstAssigneeName}
            showName={false}
            containerClassName={className}
            avatarRef={avatarRef}
          />
          <div className="assignee-circle flex items-center justify-center ml-[-5px] border border-solid border-inverse box-content !z-0 !bg-brand-lightest !text-brand text-2xs font-bold">
            {`+${assignees.length - 1}`}
          </div>
        </div>
        <Overlay
          arrowClasses="before:bg-secondary before:shadow-alert"
          className="bg-secondary p-2 rounded shadow-alert w-fit z-10"
          containerRef={document.body}
          offset={[0, 10]}
          placement={tooltipPlace}
          showOverlay={showOverlay}
          targetRef={avatarRef}
        >
          <BodyText color="text-inverse" size={BODY_TEXT_SIZES.X_SMALL}>
            {allAssigneeNames}
          </BodyText>
        </Overlay>
      </>
    );
  }

  if (assignees.length <= 1) {
    const resolvedName = getAssigneeName(assignees[0]);

    return (
      <div className="single-assignee-user-avatar-container">
        <Avatar
          size={avatarSize}
          hasNoUser={!resolvedName}
          name={resolvedName || messages?.common?.noAssignee || ""}
          showName
          nameClassName="no-styles !my-0 whitespace-nowrap break-words max-w-[calc(100%-32px)] truncate"
          containerClassName={className}
        />
      </div>
    );
  }

  const dataTip =
    assignees.length <= maxLength
      ? []
      : assignees
          .slice(maxLength, assignees.length)
          .map((assignee) => getAssigneeName(assignee))
          .join(", ");

  return (
    <div className="u-flex">
      <AvatarGroup
        names={assignees
          .slice(0, maxLength)
          .map(
            (assignee) =>
              getAssigneeName(assignee) || `${messages.common?.noName}`,
          )}
        size={avatarSize}
      />
      <>
        <div
          className={`assignee-circle items-center justify-center ml-[-5px] border border-solid border-inverse box-content !z-0 !bg-brand-lightest !text-brand text-2xs font-bold ${
            assignees.length > maxLength ? "flex" : "hidden"
          }`}
          data-tip={dataTip}
        >
          {`+${assignees.length - maxLength}`}
        </div>
      </>
      <Tooltip place={tooltipPlace} />
    </div>
  );
};

export default TicketItemAssigneesList;
